import { PublicPlan } from '@wix/ambassador-pricing-plans-v2-plan/types';
import { EndType, Image, Period, Plan } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { FormattedAdditionalFee } from '@wix/pricing-plans-tpa-api';
import {
  getSetupFeeAmount,
  getSetupFeeAmountV3,
  hasSetupFee,
  hasSetupFeeV3,
  isPlanV3,
  isRecurringVariant,
} from '@wix/pricing-plans-utils';
import { CoverImage } from '@wix/pricing-plans-utils/client-data';
import { CreateCurrencyFormatter, TFunction } from '@wix/yoshi-flow-editor';
import { NO_BREAK_SPACE } from '../constants';
import { getFormattedPriceData } from '../hooks';
import { getFormattedAdditionalFees as getFormattedAdditionalFeesV2 } from './get-formatted-additional-fees';
import { getPeriodLabel as getPeriodLabelV2 } from './getPeriodLabel';
import { getPlanValidityCycle } from './getPlanValidityCycle';

interface PlanDisplayFlags {
  recurringPlansExist: boolean;
  freeTrialDaysExist: boolean;
  setupFeesExist: boolean;
  validityCyclesExist: boolean;
  anyBenefits: boolean;
}

export function getPlanDisplayFlags(plans: (PublicPlan | Plan)[], t: TFunction): PlanDisplayFlags {
  if (isV3PlanArray(plans)) {
    return {
      recurringPlansExist: plans.some((p) => isRecurringVariant(p.pricingVariants?.[0])),
      freeTrialDaysExist: plans.some((p) => hasFreeTrialDays(p)),
      setupFeesExist: plans.some((p) => !!p.pricingVariants?.[0]?.fees?.length),
      validityCyclesExist: plans.some((p) => getPlanDuration(p, t) !== NO_BREAK_SPACE),
      anyBenefits: plans.some((p) => !!p.perks?.length),
    };
  }
  if (isV2PlanArray(plans)) {
    return {
      recurringPlansExist: plans.some((p) => !!p.pricing?.subscription),
      freeTrialDaysExist: plans.some((p) => !!p.pricing?.freeTrialDays),
      setupFeesExist: plans.some((p) => !!p.pricing?.feeConfigs?.length),
      validityCyclesExist: plans.some((p) => getPlanValidityCycle(p, t) !== NO_BREAK_SPACE),
      anyBenefits: plans.some((p) => !!p.perks?.values?.length),
    };
  }
  return {
    recurringPlansExist: false,
    freeTrialDaysExist: false,
    setupFeesExist: false,
    validityCyclesExist: false,
    anyBenefits: false,
  };
}

export function planHasRecurrence(plan: PublicPlan | Plan): boolean {
  return isPlanV3(plan) ? isRecurringVariant(plan.pricingVariants?.[0]) : !!plan.pricing?.subscription;
}

export function planHasFees(plan: PublicPlan | Plan): boolean {
  return isPlanV3(plan)
    ? (plan.pricingVariants?.[0]?.fees?.length ?? 0) > 0
    : (plan.pricing?.feeConfigs?.length ?? 0) > 0;
}

export function planHasValidity(plan: PublicPlan | Plan, t: TFunction): boolean {
  return isPlanV3(plan)
    ? getPlanDuration(plan, t) !== NO_BREAK_SPACE
    : getPlanValidityCycle(plan, t) !== NO_BREAK_SPACE;
}

export function isV3PlanArray(plans: (PublicPlan | Plan)[]): plans is Plan[] {
  return plans.length > 0 && isPlanV3(plans[0]);
}

export function isV2PlanArray(plans: (PublicPlan | Plan)[]): plans is PublicPlan[] {
  return plans.length > 0 && !isPlanV3(plans[0]);
}

export function planImageToCoverImage(image: Image | undefined): CoverImage | undefined {
  const { id, width, height } = image ?? {};
  if (!id || !width || !height) {
    return undefined;
  }
  return { uri: id, width, height };
}

export function getPlanPrice(plan: Plan) {
  return {
    value: plan.pricingVariants?.[0]?.pricingStrategies?.[0]?.flatRate?.amount,
    currency: plan.currency,
  };
}

export function getFormattedFees(params: {
  plan: PublicPlan | Plan;
  locale?: string;
  createCurrencyFormatter: CreateCurrencyFormatter;
  hideTrailingFractionZeroes?: boolean;
}): FormattedAdditionalFee[] {
  const { plan, ...rest } = params;
  return isPlanV3(plan)
    ? getFormattedAdditionalFees({ plan, ...rest })
    : getFormattedAdditionalFeesV2({ plan, ...rest });
}

export function getRecurrenceText(plan: PublicPlan | Plan, t: TFunction): string {
  if (isPlanV3(plan)) {
    return getPeriodLabel(plan, t) ?? NO_BREAK_SPACE;
  }
  return getPeriodLabelV2(plan.pricing?.subscription, t) ?? NO_BREAK_SPACE;
}

export function getPlanBenefits(plan: Plan): string[] {
  return plan.perks?.map((p) => p.description ?? '') ?? [];
}

export function getSetupFeeAmountInCents(plan: PublicPlan | Plan): number | undefined {
  if (isPlanV3(plan)) {
    return hasSetupFeeV3(plan) ? Math.round(Number(getSetupFeeAmountV3(plan)) * 100) : undefined;
  }
  return hasSetupFee(plan) ? Math.round(Number(getSetupFeeAmount(plan)) * 100) : undefined;
}

export function hasFreeTrialDays(plan: Plan): boolean {
  return !!plan.pricingVariants?.[0]?.freeTrialDays;
}

export function getFreeTrialDaysLabel(plan: Plan, t: TFunction) {
  const pricing = plan.pricingVariants?.[0];
  if (!pricing) {
    return '';
  }

  if (!pricing.freeTrialDays) {
    return '';
  }

  if (isUnlimitedPlan(plan)) {
    return t('pp.free-trial-days-unlimited', { count: pricing.freeTrialDays });
  }

  return t('pp.free-trial-days', { count: pricing.freeTrialDays });
}

export function getFormattedAdditionalFees(params: {
  plan: Plan;
  locale?: string;
  createCurrencyFormatter: CreateCurrencyFormatter;
  hideTrailingFractionZeroes?: boolean;
}): FormattedAdditionalFee[] {
  const { plan, locale, createCurrencyFormatter, hideTrailingFractionZeroes } = params;
  const pricing = plan.pricingVariants?.[0];
  if (!pricing || !pricing.fees?.length) {
    return [];
  }

  return pricing.fees
    .filter((fee) => Boolean(fee.fixedAmountOptions?.amount))
    .map((fee) => {
      const formatted = getFormattedPriceData({
        createCurrencyFormatter,
        locale,
        value: fee.fixedAmountOptions?.amount,
        currency: plan.currency,
        hideTrailingFractionZeroes,
      });

      return { name: fee.name!, amount: `${formatted.fullPrice}` };
    });
}

export function getPlanDuration(plan: Plan, t: TFunction) {
  if (isUnlimitedPlan(plan)) {
    return t('pp.plan-validity.valid-unlimited');
  } else {
    return getPlanDurationWithoutUnlimited(plan, t);
  }
}

const isUnlimitedPlan = (plan: Plan): boolean => {
  const pricing = plan.pricingVariants?.[0];
  return pricing?.billingTerms?.endType === EndType.UNTIL_CANCELLED;
};

export function getPeriodLabel(plan: Plan, t: TFunction) {
  if (!isRecurringVariant(plan.pricingVariants?.[0])) {
    return null;
  }

  const pricing = plan.pricingVariants?.[0];
  const unit = pricing?.billingTerms?.billingCycle?.period;
  const cycleDuration = parseInt(pricing?.billingTerms?.billingCycle?.count ?? '', 10);

  switch (unit) {
    case Period.DAY:
      return t('pp.period-recurrency.every-day-plural', { count: cycleDuration });
    case Period.WEEK:
      return t('pp.period-recurrency.every-week-plural', { count: cycleDuration });
    case Period.YEAR:
      return t('pp.period-recurrency.every-year-plural', { count: cycleDuration });
    case Period.MONTH:
      return t('pp.period-recurrency.every-month-plural', { count: cycleDuration });
    default:
      return '';
  }
}

function getPlanDurationWithoutUnlimited(plan: Plan, t: TFunction) {
  const pricing = plan.pricingVariants?.[0];
  const unit = pricing?.billingTerms?.billingCycle?.period;
  const singleCycleDuration = parseInt(pricing?.billingTerms?.billingCycle?.count ?? '', 10);
  const totalCycleCount = parseInt(pricing?.billingTerms?.cyclesCompletedDetails?.billingCycleCount ?? '', 10);

  if (!unit || !singleCycleDuration || !totalCycleCount) {
    return '';
  }

  const planDuration = totalCycleCount * singleCycleDuration;

  switch (unit) {
    case Period.DAY:
      return planDuration > 1
        ? t('pp.plan-validity.valid-for-days', { amount: planDuration })
        : t('pp.plan-validity.valid-for-one-day');
    case Period.WEEK:
      return planDuration > 1
        ? t('pp.plan-validity.valid-for-weeks', { amount: planDuration })
        : t('pp.plan-validity.valid-for-one-week');
    case Period.YEAR:
      return planDuration > 1
        ? t('pp.plan-validity.valid-for-years', { amount: planDuration })
        : t('pp.plan-validity.valid-for-one-year');
    default:
      return planDuration > 1
        ? t('pp.plan-validity.valid-for-months', { amount: planDuration })
        : t('pp.plan-validity.valid-for-one-month');
  }
}
